import {
  COLOR_CATALOG_CATEGORIES,
  COLOR_PICKER_DEFAULT_CATALOGS,
  COLOR_SPECIAL_COLORS,
  colorCatalogCategory,
  colorCatalogEntries,
  colorStandardCatalogKeys,
  colorStandardEntries,
  colorValueKey,
  ensureColorCatalogsLoaded,
  findCurrentColor,
  firstColor,
  getColorCatalog,
  isColorCatalogLoaded,
  listColorCatalogs,
  normalizeColorValue,
  notifyColorSelected,
  resolveColorHex,
  resolveColorLabel,
  serializeColorValue
} from './color_catalog_registry.js?v=20260720a';

export function colorPickerEscapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, char => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }[char]));
}

export function colorPickerCssValue(value, fallback = '#CFCCC8') {
  return colorPickerEscapeHtml(resolveColorHex(value, fallback));
}

export function colorPickerSwatchStyle(value, fallback = '#CFCCC8') {
  const normalized = normalizeColorValue(value, { includeSpecial: true });
  const image = normalized?.swatchImage || normalized?.texture?.image || '';
  const color = resolveColorHex(normalized || value, fallback);
  if (!image) return `background:${colorPickerEscapeHtml(color)}`;
  return [
    `background-color:${colorPickerEscapeHtml(color)}`,
    `background-image:url('${colorPickerEscapeHtml(image)}')`,
    'background-size:cover',
    'background-position:center'
  ].join(';');
}

// Aufbau der Zeilen (F6): Kategorien Standard/Farben/Dekore/Texturen aus den
// freigegebenen Katalogen. Gruppen (z. B. „Texturen“) zeigen ohne Katalogwahl
// alle ihre Kataloge gemeinsam; sonst ist immer genau ein Katalog aktiv.
export function colorPickerStructure(allowedCatalogKeys) {
  const byCategory = new Map();
  const add = (category, key, showAll = false) => {
    if (!byCategory.has(category)) byCategory.set(category, { catalogs: [], all: false });
    const entry = byCategory.get(category);
    if (!entry.catalogs.includes(key)) entry.catalogs.push(key);
    if (showAll) entry.all = true;
  };
  for (const catalog of listColorCatalogs(allowedCatalogKeys || COLOR_PICKER_DEFAULT_CATALOGS)) {
    if (catalog.type === 'group') {
      listColorCatalogs(catalog.members || []).forEach(member => add(colorCatalogCategory(member.key), member.key, true));
    } else {
      add(colorCatalogCategory(catalog.key), catalog.key);
    }
  }
  return COLOR_CATALOG_CATEGORIES
    .filter(category => byCategory.get(category.key)?.catalogs.length)
    .map(category => ({ key: category.key, label: category.label, ...byCategory.get(category.key) }))
    // Eine Gruppe mit nur einem Katalog braucht keine gemeinsame Ansicht.
    .map(category => ({ ...category, all: category.all && category.catalogs.length > 1 }));
}

function colorPickerInitialScope(structure, selected, allowedKeys) {
  const selectedKey = colorValueKey(selected);
  const standard = structure.find(category => category.key === 'standard');
  if (selected && standard && colorStandardEntries(allowedKeys).some(color => colorValueKey(color) === selectedKey)) {
    return { category: 'standard', catalog: 'standard' };
  }
  const owning = selected && structure.find(category => category.key !== 'standard' && category.catalogs.includes(selected.catalog));
  if (owning) return { category: owning.key, catalog: selected.catalog };
  const first = structure[0];
  if (!first) return { category: '', catalog: '' };
  return { category: first.key, catalog: first.all ? '' : first.catalogs[0] };
}

function colorPickerVisibleKeys(structure, categoryKey, catalogKey) {
  const category = structure.find(entry => entry.key === categoryKey);
  if (!category) return [];
  if (catalogKey && category.catalogs.includes(catalogKey)) return [catalogKey];
  return category.all ? category.catalogs : category.catalogs.slice(0, 1);
}

export function renderColorPicker(options = {}) {
  const id = options.id || `colorPicker_${Math.random().toString(36).slice(2, 8)}`;
  const allowedCatalogs = listColorCatalogs(options.allowedCatalogs || COLOR_PICKER_DEFAULT_CATALOGS);
  const allowedKeys = allowedCatalogs.map(catalog => catalog.key);
  const structure = colorPickerStructure(allowedKeys);
  const selected = normalizeColorValue(options.value, { includeSpecial: true });
  const selectedKey = colorValueKey(selected);
  const scope = colorPickerInitialScope(structure, selected, allowedKeys);
  const activeCategory = structure.find(category => category.key === scope.category) || null;
  const visibleKeys = colorPickerVisibleKeys(structure, scope.category, scope.catalog);
  const initialPanelKeys = options.renderAllCatalogs === true
    ? [...structure.flatMap(category => category.catalogs), ...visibleKeys]
    : visibleKeys;
  const selectedLabel = selected
    ? resolveColorLabel(selected)
    : (options.defaultLabel || 'Standard / keine Farbe');
  const selectedSwatchStyle = selected
    ? colorPickerSwatchStyle(selected)
    : `background:${colorPickerEscapeHtml(options.defaultSwatch || 'linear-gradient(135deg, #fff 0 42%, #cbd5e1 42% 58%, #fff 58% 100%)')}`;
  const extraAttrs = colorPickerAttributes(options.attributes || {});
  const structureAttr = JSON.stringify(structure.map(({ key, catalogs, all }) => ({ key, catalogs, all })));
  return `
    <div class="color-picker color-picker-composite" id="${colorPickerEscapeHtml(id)}" data-color-picker data-color-picker-category="${colorPickerEscapeHtml(scope.category)}" data-color-picker-catalog="${colorPickerEscapeHtml(scope.catalog)}" data-color-picker-structure="${colorPickerEscapeHtml(structureAttr)}" data-color-picker-allowed-catalogs="${colorPickerEscapeHtml(allowedKeys.join(','))}" data-color-picker-selected-key="${colorPickerEscapeHtml(selectedKey)}"${extraAttrs}>
      <div class="color-picker-current">
        <span class="color-picker-current-label">Aktuelle Auswahl</span>
        <div class="color-picker-current-value">
          <span class="color-swatch" style="${selectedSwatchStyle}"></span>
          <span>${colorPickerEscapeHtml(selectedLabel)}</span>
          ${options.allowDefault && selected ? `<button type="button" class="btn color-picker-reset-btn" data-color-picker-reset>Zurücksetzen</button>` : ''}
        </div>
      </div>
      <div class="color-picker-catalog-row color-picker-category-row"${structure.length > 1 ? '' : ' hidden'}>
        <span class="color-picker-row-label">Kategorie</span>
        <div class="color-picker-catalog-tabs" role="radiogroup" aria-label="Kategorie">
          ${structure.map(category => colorPickerCategoryTabHtml(id, category, category.key === scope.category)).join('')}
        </div>
      </div>
      <label class="color-picker-search-row">
        <input class="color-picker-search" type="search" autocomplete="off" placeholder="Farbe suchen" data-color-picker-search>
      </label>
      <div class="color-picker-group-navigation" data-color-picker-catalog-row${activeCategory && activeCategory.catalogs.length > 1 ? '' : ' hidden'}>
        <div class="color-picker-group-buttons" role="group" aria-label="Katalog">
          ${structure.map(category => colorPickerCatalogButtonsHtml(category, category.key === scope.category, scope.catalog)).join('')}
        </div>
      </div>
      <div class="color-picker-list" role="listbox">
        ${options.allowDefault ? colorPickerDefaultOptionHtml(options, !selected) : ''}
        ${colorPickerExtraOptionsHtml(options.extraColors, selected)}
        ${structure.length ? '' : '<div class="color-picker-empty" role="status">Keine Kataloge verfügbar.</div>'}
        ${[...new Set(initialPanelKeys)].map(key => colorPickerCatalogPanelHtml(key, selectedKey, {
          allowedCatalogs: allowedKeys,
          hidden: !visibleKeys.includes(key)
        })).join('')}
      </div>
    </div>
  `;
}

function colorPickerCategoryTabHtml(id, category, selected = false) {
  return `
    <label class="color-picker-catalog-tab">
      <input class="color-picker-category-input" type="radio" name="${colorPickerEscapeHtml(id)}_category" value="${colorPickerEscapeHtml(category.key)}" ${selected ? 'checked' : ''}>
      <span>${colorPickerEscapeHtml(category.label)}</span>
    </label>
  `;
}

function colorPickerCatalogButtonsHtml(category, isActiveCategory, activeCatalog) {
  if (category.catalogs.length <= 1) return '';
  return listColorCatalogs(category.catalogs).map(catalog => {
    const isSelected = isActiveCategory && catalog.key === activeCatalog;
    return `<button class="color-picker-group-btn${isSelected ? ' is-active' : ''}" type="button" data-color-picker-category-member="${colorPickerEscapeHtml(category.key)}" data-color-picker-catalog-button="${colorPickerEscapeHtml(catalog.key)}" aria-pressed="${isSelected}"${isActiveCategory ? '' : ' hidden'}>${colorPickerEscapeHtml(catalog.label || catalog.key)}</button>`;
  }).join('');
}

export function colorPickerCatalogTabHtml(id, catalog, selected = false) {
  const key = catalog?.key || '';
  return `
    <label class="color-picker-catalog-tab">
      <input class="color-picker-category-input" type="radio" name="${colorPickerEscapeHtml(id)}_category" value="${colorPickerEscapeHtml(key)}" ${selected ? 'checked' : ''}>
      <span>${colorPickerEscapeHtml(catalog?.label || key)}</span>
    </label>
  `;
}

export function colorPickerAttributes(attrs = {}) {
  return Object.entries(attrs || {})
    .filter(([, value]) => value != null)
    .map(([key, value]) => ` ${colorPickerEscapeHtml(key)}="${colorPickerEscapeHtml(value)}"`)
    .join('');
}

export function colorPickerDefaultOptionHtml(options, selected) {
  return `
    <button class="color-picker-option" type="button" role="option" data-color-picker-default="true" aria-selected="${Boolean(selected)}">
      <span class="color-swatch" style="background:${colorPickerEscapeHtml(options.defaultSwatch || 'linear-gradient(135deg, #fff 0 42%, #cbd5e1 42% 58%, #fff 58% 100%)')}"></span>
      <span>${colorPickerEscapeHtml(options.defaultLabel || 'Standard / keine Farbe')}</span>
    </button>
  `;
}

export function colorPickerExtraOptionsHtml(extraColors = [], selected = null) {
  if (!Array.isArray(extraColors) || !extraColors.length) return '';
  return extraColors
    .map(color => {
      const normalized = normalizeColorValue(color, { includeSpecial: true }) || normalizeColorValue(color.code, { includeSpecial: true });
      return normalized ? colorPickerOptionHtml(normalized, colorValueKey(selected) === colorValueKey(normalized)) : '';
    })
    .join('');
}

export function colorPickerFlatCatalogHtml(catalogKey, selected, options = {}) {
  const selectedKey = typeof selected === 'string' ? selected : colorValueKey(selected);
  const entries = catalogKey === 'standard'
    ? colorStandardEntries(options.allowedCatalogs || null)
    : colorCatalogEntries(catalogKey);
  return entries.map(color => colorPickerOptionHtml(color, selectedKey === colorValueKey(color))).join('');
}

export function colorPickerCatalogPanelHtml(catalogKey, selected, options = {}) {
  const hidden = options.hidden ? ' hidden' : '';
  const catalog = getColorCatalog(catalogKey);
  if (catalog?.type === 'group') {
    return `<div class="color-picker-catalog-panel color-picker-group-panel" data-color-catalog-panel="${colorPickerEscapeHtml(catalogKey)}"${hidden}></div>`;
  }
  const content = colorPickerFlatCatalogHtml(catalogKey, selected, options);
  const empty = catalogKey === 'standard' ? 'Keine Standardwerte in den freigegebenen Katalogen.' : 'Keine Einträge vorhanden.';
  return `
    <div class="color-picker-catalog-panel" data-color-catalog-panel="${colorPickerEscapeHtml(catalogKey)}"${hidden}>
      ${content || `<div class="color-picker-empty">${empty}</div>`}
    </div>
  `;
}

export function colorPickerNcsHtml(selected) {
  const colors = colorCatalogEntries('ncs');
  return colors.map(color => colorPickerOptionHtml(color, colorValueKey(selected) === colorValueKey(color), {
      'data-color-group': color.group || '',
      'data-color-nuance': color.nuance || ''
    })).join('');
}

export function colorPickerOptionHtml(color, selected = false, attrs = {}) {
  return `
    <button class="color-picker-option" type="button" role="option"
      data-color-picker-option="true"
      data-color-catalog="${colorPickerEscapeHtml(color.catalog)}"
      data-color-code="${colorPickerEscapeHtml(color.code)}"
      ${colorPickerAttributes(attrs)}
      aria-selected="${Boolean(selected)}">
      <span class="color-swatch" style="${colorPickerSwatchStyle(color)}"></span>
      <span>${colorPickerEscapeHtml(resolveColorLabel(color))}</span>
    </button>
  `;
}

function colorPickerAllowedKeys(picker) {
  return String(picker?.dataset?.colorPickerAllowedCatalogs || '')
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);
}

function colorPickerReadStructure(picker) {
  try {
    const parsed = JSON.parse(picker?.dataset?.colorPickerStructure || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

export function bindColorPicker(id, onSelect, options = {}) {
  const picker = typeof id === 'string' ? document.getElementById(id) : id;
  if (!picker) return;

  picker.querySelectorAll('.color-picker-category-input').forEach(input => {
    input.addEventListener('change', () => colorPickerShowScope(picker, input.value, null));
  });

  picker.addEventListener('click', event => {
    // Ein erneuter Klick auf die aktive Kategorie hebt die Katalogwahl einer
    // gemeinsamen Ansicht (z. B. alle Texturen) wieder auf.
    const categoryTab = event.target?.closest?.('.color-picker-catalog-tab');
    const categoryInput = categoryTab?.querySelector?.('.color-picker-category-input');
    if (categoryInput && picker.contains(categoryTab) && categoryInput.value === picker.dataset.colorPickerCategory) {
      const category = colorPickerReadStructure(picker).find(entry => entry.key === categoryInput.value);
      if (category?.all && picker.dataset.colorPickerCatalog) colorPickerShowScope(picker, category.key, '');
      return;
    }
    const catalogButton = event.target?.closest?.('[data-color-picker-catalog-button]');
    if (catalogButton && picker.contains(catalogButton)) {
      event.preventDefault();
      event.stopPropagation();
      const category = catalogButton.dataset.colorPickerCategoryMember;
      const catalog = catalogButton.dataset.colorPickerCatalogButton;
      const structure = colorPickerReadStructure(picker).find(entry => entry.key === category);
      const toggleOff = structure?.all && picker.dataset.colorPickerCatalog === catalog;
      colorPickerShowScope(picker, category, toggleOff ? '' : catalog);
      return;
    }
    const resetButton = event.target?.closest?.('[data-color-picker-reset]');
    if (resetButton && picker.contains(resetButton)) {
      event.preventDefault();
      event.stopPropagation();
      onSelect?.(null, null);
      return;
    }
    const option = event.target?.closest?.('[data-color-picker-option], [data-color-picker-default]');
    if (!option || !picker.contains(option)) return;
    event.preventDefault();
    event.stopPropagation();
    const value = colorPickerValueFromOption(option, options);
    const normalized = normalizeColorValue(value, { includeSpecial: true });
    onSelect?.(value, normalized);
    // Aktive Auswahl: der Core bindet den Auftrag an die aktuelle Revision (F2).
    // Erst nach onSelect, damit dessen Undo-Schritt den bisherigen Stand enthält.
    // Picker außerhalb eines Auftrags (z. B. Assetimport) setzen bindToProject: false.
    if (value && options.bindToProject !== false) notifyColorSelected(value);
  });

  picker.querySelectorAll('[data-color-picker-search]').forEach(control => {
    control.addEventListener('input', () => {
      colorPickerApplyFilters(picker);
    });
  });

  colorPickerShowScope(picker, picker.dataset.colorPickerCategory || '', picker.dataset.colorPickerCatalog ?? null);
}

// Zeigt Kategorie und Katalog; `catalog` null = Standardwahl der Kategorie,
// '' = gemeinsame Ansicht aller Kataloge einer Gruppe.
function colorPickerShowScope(picker, categoryKey, catalogKey) {
  const structure = colorPickerReadStructure(picker);
  const category = structure.find(entry => entry.key === categoryKey) || structure[0];
  if (!category) {
    colorPickerApplyFilters(picker);
    return;
  }
  let catalog = catalogKey;
  if (catalog === null || (catalog && !category.catalogs.includes(catalog)) || (catalog === '' && !category.all)) {
    catalog = category.all ? '' : category.catalogs[0];
  }
  picker.dataset.colorPickerCategory = category.key;
  picker.dataset.colorPickerCatalog = catalog;
  picker.querySelectorAll('.color-picker-category-input').forEach(input => { input.checked = input.value === category.key; });
  const catalogRow = picker.querySelector('[data-color-picker-catalog-row]');
  if (catalogRow) catalogRow.hidden = category.catalogs.length <= 1;
  picker.querySelectorAll('[data-color-picker-catalog-button]').forEach(button => {
    const inCategory = button.dataset.colorPickerCategoryMember === category.key;
    const isActive = inCategory && button.dataset.colorPickerCatalogButton === catalog;
    button.hidden = !inCategory;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
  });
  colorPickerVisibleKeys(structure, category.key, catalog).forEach(key => { colorPickerEnsureCatalogReady(picker, key); });
  colorPickerApplyFilters(picker);
}

function colorPickerLazyKeys(picker, catalogKey) {
  if (catalogKey === 'standard') return colorStandardCatalogKeys(colorPickerAllowedKeys(picker)).filter(key => !isColorCatalogLoaded(key));
  return isColorCatalogLoaded(catalogKey) ? [] : [catalogKey];
}

async function colorPickerEnsureCatalogReady(picker, catalogKey) {
  const key = String(catalogKey || '').trim();
  if (!picker || !key) return null;
  const lazyKeys = colorPickerLazyKeys(picker, key);
  if (!lazyKeys.length) return colorPickerEnsureCatalogPanel(picker, key);
  const loadingPanel = colorPickerEnsureCatalogPanel(picker, key);
  if (!loadingPanel) return null;
  loadingPanel.innerHTML = '<div class="color-picker-empty" role="status">Katalog wird geladen...</div>';
  try {
    await ensureColorCatalogsLoaded(lazyKeys);
    if (!loadingPanel.isConnected) return null;
    loadingPanel.remove();
    const panel = colorPickerEnsureCatalogPanel(picker, key);
    colorPickerApplyFilters(picker);
    return panel;
  } catch (error) {
    console.error(`Farbkatalog konnte nicht geladen werden: ${key}`, error);
    if (loadingPanel.isConnected) {
      loadingPanel.innerHTML = `<div class="color-picker-empty">Katalog konnte nicht geladen werden: ${colorPickerEscapeHtml(error?.message || error)}</div>`;
    }
    return null;
  }
}

export function colorPickerEnsureCatalogPanel(picker, catalogKey) {
  const key = String(catalogKey || '').trim();
  if (!picker || !key) return null;
  const existing = [...picker.querySelectorAll('.color-picker-catalog-panel')]
    .find(panel => panel.dataset.colorCatalogPanel === key);
  if (existing) return existing;
  const allowed = colorPickerAllowedKeys(picker);
  const isDirectlyAllowed = allowed.includes(key);
  // A member of an allowed group (e.g. "texturen_boden" under the allowed
  // "texturen" group) is reachable too, even though it isn't itself in the
  // top-level allowed-catalogs list.
  const isAllowedGroupMember = allowed.some(allowedKey => getColorCatalog(allowedKey)?.members?.includes(key));
  if (!isDirectlyAllowed && !isAllowedGroupMember) return null;
  if (!listColorCatalogs([key]).length) return null;
  const list = picker.querySelector('.color-picker-list');
  if (!list) return null;
  const template = document.createElement('template');
  const visible = colorPickerVisibleKeys(colorPickerReadStructure(picker), picker.dataset.colorPickerCategory, picker.dataset.colorPickerCatalog);
  template.innerHTML = colorPickerCatalogPanelHtml(key, picker.dataset.colorPickerSelectedKey || '', {
    allowedCatalogs: allowed,
    hidden: !visible.includes(key)
  }).trim();
  const panel = template.content.firstElementChild;
  if (!panel) return null;
  list.appendChild(panel);
  return panel;
}

// Eine Neuauswahl verwendet immer den aktuellen Katalogstand.
export function colorPickerValueFromOption(option, options = {}) {
  if (!option || option.dataset.colorPickerDefault === 'true') return null;
  const catalog = option.dataset.colorCatalog;
  const code = option.dataset.colorCode;
  const color = catalog === 'special'
    ? COLOR_SPECIAL_COLORS[String(code || '').toLowerCase()]
    : findCurrentColor(catalog, code);
  return serializeColorValue(color, options);
}

export function colorPickerDefaultValueForCatalog(catalogKey) {
  return firstColor(catalogKey);
}

export function colorPickerApplyNcsFilters(picker) {
  colorPickerApplyFilters(picker);
}

export function colorPickerApplyFilters(picker) {
  if (!picker) return;
  const structure = colorPickerReadStructure(picker);
  const categoryKey = picker.dataset.colorPickerCategory || '';
  const visible = new Set(colorPickerVisibleKeys(structure, categoryKey, picker.dataset.colorPickerCatalog || ''));
  const showAll = structure.find(entry => entry.key === categoryKey)?.all && !picker.dataset.colorPickerCatalog;
  const query = normalizeColorPickerQuery(picker.querySelector('[data-color-picker-search]')?.value || '');
  picker.querySelectorAll('.color-picker-catalog-panel').forEach(panel => {
    const key = panel.dataset.colorCatalogPanel;
    // In der gemeinsamen Ansicht werden leere Kataloge ausgeblendet.
    panel.hidden = !visible.has(key) || Boolean(showAll && !panel.querySelector('[data-color-picker-option]'));
  });
  picker.querySelectorAll('[data-color-picker-option], [data-color-picker-default]').forEach(option => {
    const panelKey = option.closest?.('[data-color-catalog-panel]')?.dataset.colorCatalogPanel;
    const catalog = option.dataset.colorCatalog || '';
    // Optionen außerhalb der Katalogbereiche (Zusatzfarben) gehören zu ihrem
    // Katalog; Sonderfarben stehen bei Farben und Standard.
    const matchesScope = panelKey
      ? visible.has(panelKey)
      : visible.has(catalog) || (catalog === 'special' && ['standard', 'farben'].includes(categoryKey));
    const label = normalizeColorPickerQuery(option.textContent || '');
    const code = normalizeColorPickerQuery(option.dataset.colorCode || '');
    const matchesQuery = !query || label.includes(query) || code.includes(query);
    option.hidden = !(matchesScope && matchesQuery);
  });
}

function normalizeColorPickerQuery(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/^RAL\s*/, 'RAL')
    .replace(/^NCS\s+/, 'NCS ')
    .replace(/\s+/g, ' ');
}

// Rebuild only affected entry panels; keep selected project values and handlers.
if (typeof document !== 'undefined') document.addEventListener('ks-color-catalogs-changed', event => {
  const changed = new Set(event.detail?.keys || []);
  document.querySelectorAll('[data-color-picker]').forEach(picker => {
    picker.querySelectorAll('[data-color-catalog-panel]').forEach(panel => {
      if (changed.has(panel.dataset.colorCatalogPanel)) panel.remove();
    });
    const visible = colorPickerVisibleKeys(colorPickerReadStructure(picker), picker.dataset.colorPickerCategory, picker.dataset.colorPickerCatalog || '');
    visible.filter(key => changed.has(key)).forEach(key => colorPickerEnsureCatalogReady(picker, key));
    colorPickerApplyFilters(picker);
  });
});
